import React, { MouseEvent, TouchEvent } from 'react'
import styled from '@emotion/styled'
import { useSelector } from 'react-redux'
import { Link } from 'react-router'
import { css, Global } from '@emotion/react'
import { FeatureFlags, hexColors } from '@mobi/settings'
import {
  isNativeAppMajorVersionBelow,
  isReactNativeApp,
  isReactNativeAndroid,
  AndroidSafeAreaInsets,
} from '@mobi/web-native-comms/web'
import { SvgLazy } from '@mobi/component-library/Assets/Svg/SvgLazy'
import { colors, font, measurements } from '@mobi/component-library/Foundations'
import { Icon as IconLegacy } from '@mobi/component-library/Legacy/Icon'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { navigateToLastRacesPath } from '@core/State/Navigation/helpers/navigateToLastRacesPath'
import { AppRoutes } from '@core/AppRoutes'
import { KAMBI_ROOT_ROUTE } from '@core/Areas/KambiClient/constants'
import { selectIsSkyVideoPlayerOpen } from '@core/Areas/SkyVideoPlayer/Store/selectors'
import { trackBottomNavigationClicked } from '@core/Areas/Home/analytics'
import { navigateToSkyRacePage } from '@core/Areas/RaceCardSky/helpers/navigateToSkyRacePage'
import { useFeature } from '@core/Utils/hooks/useFeature'
import { shouldApplySafeAreaBottomPadding } from '@core/Settings/cssConditions'
import { NativeAppVersionWithRemovedTabNav } from '@core/Settings/constants'
import { useActiveTab } from './hooks/useActiveTab'
import { useRadio } from './hooks/useRadio'
import type { NavigationTabs } from './types'
import { getKambiSportsOfferingIntegrationOverrideSetting } from '@core/Areas/Settings/Store/selectors'

export const TabNavigation = () => {
  /** Phasing out native bottom tab navigator, in favour of single implementation. New native
   *  versions will use web tab navigation.
   *
   *  useActiveTab will still send comms to legacy native versions for backward compatibility.
   */
  const shouldUseLegacyTabNavigation =
    isReactNativeApp && isNativeAppMajorVersionBelow(NativeAppVersionWithRemovedTabNav)

  const activeTab = useActiveTab(shouldUseLegacyTabNavigation)

  if (shouldUseLegacyTabNavigation) {
    return null
  }

  return <TabNavigationComponent activeTab={activeTab} />
}

// ===============
// Local Component
// ===============

const enum LocalConstants {
  ActiveTabClassName = 'js-bottom-tab-nav--active',
}

const bottomSafeAreaInset =
  isReactNativeAndroid && AndroidSafeAreaInsets?.bottom
    ? `${AndroidSafeAreaInsets.bottom}px`
    : 'env(safe-area-inset-bottom, 0px)'

const TabNavigationComponent: React.FC<{ activeTab: NavigationTabs }> = ({ activeTab }) => {
  const bottomNavRef = React.useRef<HTMLDivElement>(null)

  const isSkyVideoPlayerOpen = useSelector(selectIsSkyVideoPlayerOpen)
  const isBetLoopFeatureActive = useFeature('content-hub-follow-me')
  const isKambiSportsOfferingFeatureActive = useFeature(
    FeatureFlags.KAMBI_SPORTS_OFFERING_INTEGRATION.key
  )
  const isLocallyEnabled = useSelector(getKambiSportsOfferingIntegrationOverrideSetting)

  const isKambiSportsOfferingEnabled = isKambiSportsOfferingFeatureActive && isLocallyEnabled

  const { isRadioBusy } = useRadio(isSkyVideoPlayerOpen)

  const handleSkyRacingClick = () => {
    navigateToSkyRacePage()
    trackBottomNavigationClicked('Watch')
  }

  const handleRacesClick = (e: React.MouseEvent) => {
    navigateToLastRacesPath()
    onNavItemClicked(e)
  }

  const isSkyRacingTabActive = activeTab === 'SkyRacing'

  const bodyBottomPadHeight = shouldApplySafeAreaBottomPadding
    ? `calc(${measurements.mobi.bottomTabsHeight} + ${bottomSafeAreaInset})`
    : 0

  return (
    <ContainerStyled ref={bottomNavRef} id='bottomTabNavigator'>
      <Global
        styles={css`
          :root {
            --safe-area-inset-bottom: ${bottomSafeAreaInset};
          }

          body {
            border-bottom: ${bodyBottomPadHeight} solid
              ${bottomNavRef.current
                ? getComputedStyle(bottomNavRef.current).backgroundColor
                : hexColors.white};
          }
        `}
      />

      <Link
        to='/'
        data-tid-bottom-nav-home
        onClick={onNavItemClicked}
        className={activeTab === 'Home' ? LocalConstants.ActiveTabClassName : ''}
      >
        <IconLegacy type='house' size='2.5rem' color='currentColor' />
        <strong>Home</strong>
      </Link>

      <button
        type='button'
        data-tid-bottom-nav-races
        onClick={handleRacesClick}
        className={activeTab === 'Races' ? LocalConstants.ActiveTabClassName : ''}
      >
        <IconLegacy type='racesall' size='2.5rem' color='currentColor' />
        <strong>Races</strong>
      </button>

      {isBetLoopFeatureActive && (
        <Link
          to={AppRoutes.BetLoop}
          onClick={onNavItemClicked}
          className={activeTab === 'BetLoop' ? LocalConstants.ActiveTabClassName : ''}
        >
          <Icon name='CustomBetLoopLogo' size='2.5rem' color='currentColor' />
          <strong>Bet Loop</strong>
        </Link>
      )}

      <Link
        to={isKambiSportsOfferingEnabled ? AppRoutes.Sports : `/${KAMBI_ROOT_ROUTE}/home`}
        data-tid-bottom-nav-sports
        onClick={onNavItemClicked}
        className={activeTab === 'Sports' ? LocalConstants.ActiveTabClassName : ''}
      >
        <IconLegacy type='FBL' size='2.5rem' color='currentColor' />
        <strong>Sports</strong>
      </Link>

      <button
        type='button'
        disabled={isRadioBusy}
        data-tid-bottom-nav-watch
        onClick={handleSkyRacingClick}
      >
        <SvgLazy
          name='SkyRacing'
          color={isSkyRacingTabActive ? '' : hexColors.grey}
          height='2.5rem'
        />
        <strong className={isSkyRacingTabActive ? LocalConstants.ActiveTabClassName : ''}>
          Racing
        </strong>
      </button>
    </ContainerStyled>
  )
}

// =============
// Local Helpers
// =============

function onNavItemClicked(e: MouseEvent | TouchEvent) {
  const text = (e.currentTarget?.textContent ?? '').trim()
  if (text) trackBottomNavigationClicked(text)
}

// ======
// Styles
// ======

const ContainerStyled = styled.div({
  position: 'fixed',
  zIndex: 2,
  bottom: 0,
  left: 0,
  right: 0,
  display: 'flex',
  alignItems: 'stretch',
  margin: '0 auto',
  height: measurements.mobi.bottomTabsHeight,
  width: '100%',
  minWidth: measurements.mobi.minWidth,
  maxWidth: measurements.mobi.maxWidth,
  paddingBottom: shouldApplySafeAreaBottomPadding ? bottomSafeAreaInset : 0,
  backgroundColor: '#FEFEFE',
  color: hexColors.grey,
  borderTop: `1px solid ${hexColors.gainsboro}`,
  transform: 'translate3d(0,0,0)',

  strong: {
    paddingTop: '0.2rem',
    fontFamily: font.family.primary,
    fontSize: font.size.xs.fontSize,
    letterSpacing: font.size.xs.letterSpacing,
    lineHeight: font.size.xs.lineHeight,
    fontWeight: font.weight.regular,
  },

  'button, a': {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textDecoration: 'none',
    background: 'none',
    color: 'inherit',
    border: 0,
    margin: 0,
    padding: 0,
    WebkitTapHighlightColor: 'transparent',

    '&:active': {
      background: hexColors.veryLightGrey,
    },
  },

  [`.${LocalConstants.ActiveTabClassName}`]: {
    color: colors.lavender[600],
  },

  button: {
    position: 'relative',

    '> span': {
      position: 'absolute',
      top: '0.2rem',
      left: '50%',
      transform: 'translateX(-50%)',
      marginLeft: '-0.9rem',
      padding: '0.2rem 0.3rem',
      fontSize: '0.6rem',
      fontStyle: 'italic',
      fontWeight: 700,
      lineHeight: 1,
      textTransform: 'uppercase',
      borderRadius: '0.2rem',
      background: colors.error[500],
      color: colors.white,
    },
  },
})
